import { Appraisal } from "@/models/appraisal.entity";
import { AttachmentRequest } from "@/models/attachment-request.entity";
import { Call } from "@/models/call.entity";
import { ClearanceApproval } from "@/models/clearance-approval.entity";
import { Clearance } from "@/models/clearance.entity";
import { CompanyGroup } from "@/models/company-group.entity";
import { Company } from "@/models/company.entity";
import { Contract } from "@/models/contract.entity";
import { Cost } from "@/models/cost.entity";
import { Fleet } from "@/models/fleet.entity";
import { Group } from "@/models/group.entity";
import { Handover } from "@/models/handover.entity";
import { Invoice } from "@/models/invoice.entity";
import { Lead } from "@/models/lead.entity";
import { PartnerMessage } from "@/models/partner-message.entity";
import { PersonGroup } from "@/models/person-group.entity";
import { Person } from "@/models/person.entity";
import { IReference } from "@/models/reference.entity";
import { Rental } from "@/models/rental.entity";
import { RepairEstimate } from "@/models/repair-estimate.entity";
import { ReportGroup } from "@/models/report-group.entity";
import { Report } from "@/models/report.entity";
import { SignDocument } from "@/models/sign-document.entity";
import { SignRequest } from "@/models/sign-request.entity";
import { SnapshotSignRequest } from "@/models/snapshot-sign-request.entity";
import { Snapshot } from "@/models/snapshot.entity";
import { Ticket } from "@/models/ticket.entity";
import { PartnerUser } from "@/models/user.entity";
import { Vehicle } from "@/models/vehicle.entity";
import { MrfiktivReferenceGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { CustomerAccountModule } from "@/store/modules/customer-account.store";
import { EventModule } from "@/store/modules/event.store";
import { RefSelectType } from "@/store/modules/fleet-aggregation.store";
import { RefTypeMap } from "@/store/modules/refs.store";

/**
 * Copy text to clipboard
 * @param text Text to copy to clipboard
 */

export async function fetchReference<T = RefSelectType, TParams = undefined>(
  partnerId: string,
  ref: IReference | MrfiktivReferenceGen,
  params?: TParams
): Promise<T | undefined> {
  let found;

  const refType = ref.refType;
  const id = ref.refId;

  if (!params) {
    params = {} as TParams;
  }

  found = RefTypeMap.get(refType as ResourceEnum)?.module.filteredAndSorted.find((e: RefSelectType) => e.id === id);
  if (found) return found;

  // if the reference is not found in the local store, try to get it from the backend
  switch (refType as ResourceEnum) {
    case ResourceEnum.EVENT:
      found = await EventModule.getOne({ partnerId, eventId: id, ...params });
      break;
    case ResourceEnum.VEHICLE:
      found = await new Vehicle({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.REPORT:
      found = await new Report({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.REPORT_GROUP:
      found = await new ReportGroup({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.TICKET:
      found = await new Ticket({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.DOCUMENT:
      found = await new SignDocument({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.SNAPSHOT:
      found = await new Snapshot({ id, handoverId: id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.SNAPSHOT_SIGN_REQUEST:
      found = await new SnapshotSignRequest({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.SIGN:
      found = await new SignRequest({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.ATTACHMENT_REQUEST:
      found = await new AttachmentRequest({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.MESSAGE:
      found = await new PartnerMessage({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.COST:
      found = await new Cost({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.COMPANY:
      found = await new Company({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.COMPANY_GROUP:
      found = await new CompanyGroup({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.PERSON:
      found = await new Person({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.PERSON_GROUP:
      found = await new PersonGroup({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.CONTRACT:
      found = await new Contract({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.GROUP:
      found = await new Group({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.FLEET:
      found = await new Fleet({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.REPAIR_ESTIMATE:
      found = await new RepairEstimate({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.CLEARANCE_APPROVAL:
      found = await new ClearanceApproval({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.CLEARANCE:
      found = await new Clearance({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.RENTAL:
      found = await new Rental({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.APPRAISAL:
      found = await new Appraisal({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.INVOICE:
      found = await new Invoice({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.HANDOVER:
      found = await new Handover({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.USER:
      found = await new PartnerUser({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.CUSTOMER_DATA:
      found = await CustomerAccountModule.getOne({ partnerId, id: id });
      break;
    case ResourceEnum.LEAD:
      found = await new Lead({ id, partnerId, ...params }).fetch();
      break;
    case ResourceEnum.CALL:
      found = await new Call({ id, partnerId, ...params }).fetch();
      break;
  }

  if (found) return found as T | undefined;
}
